import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Space } from 'antd';
import type { EmpleadosCatalogos, EstadoListado } from '../../api/empleadosApi';

/** Filtros del listado. Viven solo en memoria: nada de esto se escribe en la URL ni en el almacenamiento (C8). */
export interface FiltrosListado {
  texto: string;
  estado: EstadoListado;
  idDelegacion?: number;
  idZona?: number;
  puesto?: string;
  departamento?: string;
}

export const FILTROS_INICIALES: FiltrosListado = { texto: '', estado: 'activos' };

/** Hay algún filtro distinto del de partida (solo activos, sin más). */
export const hayFiltrosActivos = (f: FiltrosListado): boolean =>
  f.texto !== '' || f.estado !== FILTROS_INICIALES.estado
  || f.idDelegacion !== undefined || f.idZona !== undefined || f.puesto !== undefined || f.departamento !== undefined;

interface Props {
  filtros: FiltrosListado;
  /** Listas para los desplegables; `null` mientras llegan o si fallaron. */
  catalogos: EmpleadosCatalogos | null;
  /** Un cambio de filtro: el listado vuelve a la página 1. */
  onCambio: (cambio: Partial<FiltrosListado>) => void;
  onLimpiar: () => void;
}

const ESTADOS: { value: EstadoListado; label: string }[] = [
  { value: 'activos', label: 'Activos' },
  { value: 'bajas', label: 'De baja' },
  { value: 'todos', label: 'Todos' },
];

const comoOpciones = (valores?: string[]) => (valores ?? []).map((v) => ({ value: v, label: v }));

/**
 * Panel de filtros del listado: estado, delegación, zona (las de la delegación elegida), puesto y departamento,
 * combinables con el buscador. Se reparte en varias líneas cuando la pantalla es estrecha.
 */
export const FiltrosEmpleados: React.FC<Props> = ({ filtros, catalogos, onCambio, onLimpiar }) => {
  // El texto que se teclea no filtra hasta pulsar buscar; al limpiar los filtros vuelve a vaciarse.
  const [escrito, setEscrito] = useState(filtros.texto);
  useEffect(() => { setEscrito(filtros.texto); }, [filtros.texto]);

  const zonas = useMemo(
    () => (catalogos?.zonas ?? [])
      .filter((z) => filtros.idDelegacion !== undefined && z.idDelegacion === filtros.idDelegacion)
      .map((z) => ({ value: z.idZona, label: z.descripcion })),
    [catalogos, filtros.idDelegacion],
  );

  const sinListas = !catalogos;
  // Una lista que llegó vacía (la columna no está informada en la base) no abre un desplegable sin nada: se desactiva y lo dice.
  const sinPuestos = !sinListas && !catalogos.puestos?.length && filtros.puesto === undefined;
  const sinDepartamentos = !sinListas && !catalogos.departamentos?.length && filtros.departamento === undefined;

  return (
    <Space wrap size={[12, 12]} style={{ marginBottom: 12 }}>
      <Input.Search
        allowClear
        aria-label="Buscar"
        placeholder="Nombre, email o código"
        maxLength={100}
        style={{ width: 280, maxWidth: '100%' }}
        value={escrito}
        onChange={(e) => setEscrito(e.target.value)}
        onSearch={(v) => { setEscrito(v); onCambio({ texto: v.trim() }); }}
      />
      <Select
        aria-label="Estado"
        style={{ width: 120 }}
        value={filtros.estado}
        options={ESTADOS}
        onChange={(v: EstadoListado) => onCambio({ estado: v })}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        aria-label="Delegación"
        placeholder="Delegación"
        style={{ width: 190 }}
        disabled={sinListas}
        value={filtros.idDelegacion}
        options={(catalogos?.delegaciones ?? []).map((d) => ({ value: d.idDelegacion, label: d.descripcion }))}
        onChange={(v?: number) => onCambio({ idDelegacion: v, idZona: undefined })}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        aria-label="Zona"
        placeholder={filtros.idDelegacion === undefined ? 'Zona (elige delegación)' : 'Zona'}
        style={{ width: 200 }}
        disabled={sinListas || filtros.idDelegacion === undefined}
        value={filtros.idZona}
        options={zonas}
        onChange={(v?: number) => onCambio({ idZona: v })}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        aria-label="Puesto"
        placeholder={sinPuestos ? 'Sin puestos informados' : 'Puesto'}
        style={{ width: 200 }}
        disabled={sinListas || sinPuestos}
        value={filtros.puesto}
        options={comoOpciones(catalogos?.puestos)}
        onChange={(v?: string) => onCambio({ puesto: v })}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        aria-label="Departamento"
        placeholder={sinDepartamentos ? 'Sin departamentos informados' : 'Departamento'}
        style={{ width: 200 }}
        disabled={sinListas || sinDepartamentos}
        value={filtros.departamento}
        options={comoOpciones(catalogos?.departamentos)}
        onChange={(v?: string) => onCambio({ departamento: v })}
      />
      <Button onClick={onLimpiar} disabled={!hayFiltrosActivos(filtros) && escrito === ''}>
        Limpiar filtros
      </Button>
    </Space>
  );
};
